import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Select2Multiple from 'v-select2-multiple-component';
import MainLayout from "@/components/layout/admin/MainLayout.vue";
import axios from 'axios';
import CONFIG from "@/assets/js/config.js";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { required, decimal } from "vuelidate/lib/validators";

export default {
  name: "skillEdit",
  components: {
    MainLayout,
    flatPickr,
    Select2Multiple,
    Loading
  },

  data() {
    return {
      employeeableId: this.$route.params.employeeableId,
      type: this.$route.params.type,
      empData: {
        employeeableId: null,
        empNumber: '',
        positionId: null,
        startDate: '',
        basicSalary: '0.00',
        basicSalaryAttrId: null,
        employmentType: CONFIG.EMPLOYMENT_TYPE.FULL_TIME,
        totalSalary: 0,
        salaryComponents: [],
        updatedUserName: '',
      },
      skills: [],
      employeeList: [],
      employeeOptions: [],
      positionOptions: [],
      submitted: false,
      disabled: false,
      isLoading: true,
      dataNotFound: false,
      noDataMsg: CONFIG.NO_DATA_EXIST,
      CONFIG,
    };
  },

  validations() {
    const rules = {
      employeeableId: { required },
      positionId: { required },
      startDate: { required },
      basicSalary: {
        required,
        decimal,
        maxDigits: (value) => !value || /^\d{1,8}(\.\d{1,2})?$/.test(value),
        twoDecimal: (value) => !value || /^\d+(\.\d{1,2})?$/.test(value),
      },
    };
    if (this.isFullTimeEmployee) {
      rules.salaryComponents = {
        $each: {
          amount: {
            decimal,
            maxDigits: (value) => !value || /^\d{1,8}(\.\d{1,2})?$/.test(value),
            twoDecimal: (value) => !value || /^\d+(\.\d{1,2})?$/.test(value),
          }
        }
      };
    }
    return { empData: rules };
  },

  computed: {
    hasSkillData() {
      return !this.dataNotFound && this.empData?.employeeableId != null;
    },
    isFullTimeEmployee() {
      return this.empData.employmentType == CONFIG.EMPLOYMENT_TYPE.FULL_TIME;
    },
    isBasicSalaryEditable() {
      return this.empData.employmentType == CONFIG.EMPLOYMENT_TYPE.DAILY_PART_TIME
        || this.empData.employmentType == CONFIG.EMPLOYMENT_TYPE.HOURLY_PART_TIME;
    },
    basicSalaryLabel() {
      if (this.empData.employmentType == CONFIG.EMPLOYMENT_TYPE.DAILY_PART_TIME) {
        return 'Daily Rate';
      }
      if (this.empData.employmentType == CONFIG.EMPLOYMENT_TYPE.HOURLY_PART_TIME) {
        return 'Hourly Rate';
      }
      return 'Basic Salary';
    },
  },

  watch: {
    'empData.positionId': function(newVal, oldVal) {
      if (this.isLoading) {
        return;
      }
      if (oldVal !== null && String(oldVal) !== String(newVal)) {
        this.empData.startDate = '';
      }
      if (!newVal || this.positionOptions.length === 0) {
        return;
      }
      const selectedPosition = this.positionOptions.find(p => p.id == newVal);
      if (!selectedPosition) {
        return;
      }

      if (this.isFullTimeEmployee) {
        this.empData.basicSalary = this.resolvePositionBasicSalary(selectedPosition, this.empData.employmentType);
        this.calculateTotal();
        return;
      }

      if (oldVal != null && String(oldVal) !== String(newVal)) {
        this.empData.basicSalary = this.resolvePositionBasicSalary(selectedPosition, this.empData.employmentType);
        this.calculateTotal();
      }
    },
    '$route.params.employeeableId'(newId, oldId) {
      if (oldId !== undefined && newId !== oldId) {
        this.loadSkillEdit();
      }
    },
    '$route.params.type'(newType, oldType) {
      if (oldType !== undefined && newType !== oldType) {
        this.loadSkillEdit();
      }
    },
  },
  methods: {
    getEmptyEmpData() {
      return {
        employeeableId: null,
        empNumber: '',
        positionId: null,
        startDate: '',
        basicSalary: '0.00',
        basicSalaryAttrId: null,
        employmentType: CONFIG.EMPLOYMENT_TYPE.FULL_TIME,
        totalSalary: 0,
        salaryComponents: [],
        updatedUserName: '',
      };
    },

    markDataNotFound() {
      this.empData = this.getEmptyEmpData();
      this.employeeOptions = [];
      this.positionOptions = [];
      this.dataNotFound = true;
      this.isLoading = false;
    },

    loadSkillEdit() {
      this.isLoading = true;
      this.dataNotFound = false;
      this.empData = this.getEmptyEmpData();
      this.employeeOptions = [];
      this.positionOptions = [];
      this.employeeableId = this.$route.params.employeeableId;
      this.type = this.$route.params.type;
      return this.getEditData();
    },

    resolvePositionBasicSalary(position, employmentType) {
      if (employmentType == CONFIG.EMPLOYMENT_TYPE.DAILY_PART_TIME) {
        return position.daily_salary ? parseFloat(position.daily_salary).toFixed(2) : '0.00';
      }
      if (employmentType == CONFIG.EMPLOYMENT_TYPE.HOURLY_PART_TIME) {
        return position.hourly_salary ? parseFloat(position.hourly_salary).toFixed(2) : '0.00';
      }
      return position.basic_salary ? parseFloat(position.basic_salary).toFixed(2) : '0.00';
    },

    calculateTotal() {
      const basic = parseFloat(this.empData.basicSalary) || 0;
      const componentsSum = this.empData.salaryComponents.reduce((acc, item) => {
        return acc + (parseFloat(item.amount) || 0);
      }, 0);

      const total = basic + componentsSum;
      this.empData.totalSalary = total.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    async getEditData() {
      const validTypes = [
        this.CONFIG.EMPLOYEE_TYPE.ADMIN.toString(),
        this.CONFIG.EMPLOYEE_TYPE.TEACHER.toString()
      ];
  
      if (!this.employeeableId || !this.type || !validTypes.includes(this.type.toString())) {
        this.markDataNotFound();
        return;
      }
      this.isLoading = true;
      try {
        const response = await axios.get(`/skill/edit/${this.employeeableId}/${this.type}`);
        if (!response.data || !response.data.detail || response.data.detail.employee_id == null) {
          this.markDataNotFound();
          return;
        }
        const data = response.data;
        this.employeeOptions = data.employeeOptions || [];
        this.positionOptions = data.positionOptions || [];

        this.empData.employeeableId = data.detail.employee_id.toString();
        this.empData.empNumber = data.detail.emp_no;
        this.empData.startDate = data.detail.start_date;
        this.empData.employmentType = data.detail.employment_type || CONFIG.EMPLOYMENT_TYPE.FULL_TIME;
        this.empData.basicSalary = parseFloat(data.detail.basic_salary || 0).toFixed(2);
        this.empData.basicSalaryAttrId = data.detail.basic_salary_id;
        this.empData.salaryComponents = (data.detail.components || []).map(comp => ({
          ...comp,
          amount: parseFloat(comp.amount).toFixed(2)
        }));
        this.empData.positionId = data.detail.position_id ? data.detail.position_id.toString() : null;
        this.empData.updatedUserName = data.detail.updated_user_name || '';
        this.dataNotFound = false;

        this.calculateTotal();
      } catch (error) {
        if (error.response?.status == 401) {
          this.$store.commit('logout');
          window.location.replace('login');
          return;
        }
        console.error("Failed To Fetch Data:", error);
        this.markDataNotFound();
      } finally {
        this.isLoading = false;
      }
    },

    async update() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.isLoading = true;
      try {
        const allAttributes = [
          {
            attribute_id: this.empData.basicSalaryAttrId,
            amount: this.empData.basicSalary
          },
          ...this.empData.salaryComponents.map(item => ({
            attribute_id: item.attribute_id,
            amount: item.amount
          }))
        ];

        const payload = {
          employeeable_id: this.employeeableId,
          employee_type: this.type,
          position_id: this.empData.positionId,
          start_date: this.empData.startDate,
          basic_salary: this.empData.basicSalary,
          basic_salary_attr_id: this.empData.basicSalaryAttrId,
          skills: this.empData.salaryComponents.map(item => ({
            attribute_id: item.attribute_id,
            amount: item.amount
          })),
          all_attributes: allAttributes
        };
        const response = await axios.post(`/skill/update/${this.employeeableId}/${this.type}`, payload);
        if (response.status === 200) {
          this.$notification.success("Skill Data Updated Successfully", {
            timer: 3,
            position: "bottomCenter"
          });
          this.$router.push({
            name: 'skillList'
          })
        }
      } catch (error) {
        console.error("Failed to update skill data:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    this.loadSkillEdit();
  },
};
